import { Box, Stack, Typography } from "@mui/material";
import { useHeatSummaryStyles } from "components/layout/heat/heatStyles";
import { T } from "components/util/t";
import {
    PendingLeaderboardFields,
    Progression,
} from "components/events/judging/pending/usePendingLeaderboard";
import { mergeSx, Sx } from "utils/sx";
import { useCompetitorsAndResultsById } from "components/layout/heat/useCompetitorsById";
import { Fragment } from "react/jsx-runtime";
import { usePendingScoresAndTies } from "components/events/judging/pending/usePendingScoresAndTies";
import { PendingLeaderboardActionArea } from "components/events/judging/pending/PendingLeaderboardActionArea";
import { ProgressionCutLine } from "components/events/judging/pending/ProgressionCutLine";
import { PendingLeaderboardRow } from "components/events/judging/pending/PendingLeaderboardItem";
import { useAutoAnimate } from "@formkit/auto-animate/react";

const stickyHeadingStyles: Sx = {
    position: "sticky",
    top: 0,
    zIndex: 1,
};

const listStyles: Sx = (theme) => ({
    borderBottom: `1px solid ${theme.palette.border.light}`,
    padding: theme.spacing(2, 2, 15.5, 2),
    margin: 0,
    listStyle: "none",
    maxHeight: "100%",
    overflowY: "auto",

    // first item after a tied group
    "& [data-tied=true] + [data-tied=false]": {
        borderTop: "1px solid transparent",
    },

    // any items in a non-tied group
    "& [data-tied=false] + [data-tied=false]": {
        borderTop: `1px solid ${theme.palette.border.light}`,
    },
});

export const PendingLeaderboard: React.FC<{
    leaderboard: PendingLeaderboardFields;
    leaderboardWithPending: PendingLeaderboardFields;
    progression: Progression;
    Header: React.ReactNode;
}> = ({ leaderboard, leaderboardWithPending, Header, progression }) => {
    const competitorsAndResultsById = useCompetitorsAndResultsById(
        leaderboardWithPending.competitors,
        leaderboard.result,
    );

    const [autoAnimateParent] = useAutoAnimate();
    const pendingScoresAndTies = usePendingScoresAndTies(
        leaderboardWithPending,
    );

    return (
        <>
            <Stack sx={stickyHeadingStyles}>
                {Header}
                <PendingLeaderboardActionArea
                    actionItems={pendingScoresAndTies.actionItems}
                    competitorsAndResultsById={competitorsAndResultsById}
                    heat={leaderboardWithPending}
                />
            </Stack>
            <Box
                component="ul"
                sx={mergeSx(listStyles)}
                ref={autoAnimateParent}
                id="pending-leaderboard"
            >
                {leaderboardWithPending.result.length === 0 ? (
                    <EmptyState />
                ) : (
                    <>
                        <Typography
                            component="div"
                            variant="label2"
                            color="textSecondary"
                            textTransform="uppercase"
                        >
                            Leaderboard
                        </Typography>
                        {leaderboardWithPending.result.map(
                            (result, resultIndex) => {
                                const resultData =
                                    competitorsAndResultsById[result.athleteId];
                                if (!resultData) return null;
                                const tieGroup = result.place
                                    ? pendingScoresAndTies.pendingTiedPlaces[
                                          result.place
                                      ]
                                    : undefined;

                                const tied = !!tieGroup;
                                const indexInTiedGroup =
                                    tieGroup?.results?.findIndex(
                                        (result) =>
                                            result.resultIndex === resultIndex,
                                    );
                                const tieResult =
                                    tieGroup?.results?.[
                                        indexInTiedGroup as number
                                    ];

                                const firstTied = indexInTiedGroup === 0;
                                const lastTied =
                                    indexInTiedGroup &&
                                    indexInTiedGroup + 1 ===
                                        tieGroup?.results?.length;

                                const pendingResult =
                                    tieResult ||
                                    pendingScoresAndTies.pendingScorePlaces[
                                        result.place as number
                                    ] ||
                                    {};

                                return (
                                    <Fragment key={result.athleteId}>
                                        {leaderboardWithPending.round.toLowerCase() !==
                                            "final" && (
                                            <ProgressionCutLine
                                                progression={progression}
                                                index={resultIndex}
                                                tied={tied && !firstTied}
                                                pending={tieGroup?.isPending}
                                            />
                                        )}

                                        <PendingLeaderboardRow
                                            competitor={resultData.competitor}
                                            result={resultData.result}
                                            pendingResult={result}
                                            heat={leaderboardWithPending}
                                            tied={
                                                firstTied
                                                    ? "first"
                                                    : lastTied
                                                      ? "last"
                                                      : tied
                                            }
                                            pendingRide={pendingResult.ride}
                                            isPendingTie={tieGroup?.isPending}
                                        />
                                    </Fragment>
                                );
                            },
                        )}
                    </>
                )}
            </Box>
        </>
    );
};

const EmptyState: React.FC = () => {
    const classes = useHeatSummaryStyles();

    return (
        <li className={classes.row}>
            <Typography variant="body1">
                <T>Athlete lineup is coming soon, stay tuned.</T>
            </Typography>
        </li>
    );
};
